import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Col, Row } from 'react-bootstrap';
import { Bookmark, BookmarkFill, Copy, Download, FileEarmarkText } from 'react-bootstrap-icons';
import LinesEllipsis from 'react-lines-ellipsis';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';

import { AccountBookmarkModel, BOOKMARK_TYPE } from '@/lib/models';
import { accountService, documentService } from '@/lib/services';
import {
	createUseThemedStyles,
	useAccount,
	useAlert,
	useAnalytics,
	useCopyTextToClipboard,
	useHandleError,
	useNavigationConfig,
} from '@/hooks';
import { LoadingButton } from '@/components';

const useStyles = createUseThemedStyles((theme) => ({
	imageOuter: {
		width: '100%',
		paddingBottom: '66.66%',
		backgroundSize: 'cover',
		backgroundPosition: 'center',
		backgroundRepeat: 'no-repeat',
		backgroundColor: theme.colors.gray300,
	},
	tagItem: {
		'&:not(:last-child)::after': {
			content: '", "',
			whiteSpace: 'pre',
		},
	},
}));

interface ResearchListItemProps {
	documentId: string;
	category: string;
	fileType?: string;
	viewUrl: string;
	downloadUrl: string;
	shareUrl: string;
	canDownload: boolean;
	bookmarkType: BOOKMARK_TYPE;
	isBookmarked: boolean;
	to: string;
	title: string;
	description: string;
	imageSrc: string;
	date: string;
	tags: string[];
	onChange?(bookmark: AccountBookmarkModel): void;
	isPodcast?: boolean;
}

const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

export const ResearchListItem = ({
	documentId,
	category,
	viewUrl,
	downloadUrl,
	shareUrl,
	canDownload,
	bookmarkType,
	isBookmarked,
	to,
	title,
	description,
	imageSrc,
	date,
	tags,
	onChange,
	isPodcast,
}: ResearchListItemProps) => {
	const classes = useStyles();
	const navigate = useNavigate();
	const { account } = useAccount();
	const { copyTextToClipboard } = useCopyTextToClipboard();
	const { navigationConfig } = useNavigationConfig();
	const handleError = useHandleError();
	const { showAlert } = useAlert();
	const [isBookmarking, setIsBookmarking] = useState(false);
	const analytics = useAnalytics();

	const handleViewClick = useCallback(async () => {
		try {
			if (!isPodcast) {
				await documentService.trackDocumentView(documentId).fetch();
			}
		} catch (error) {
			// fail silently because we dont need to report a failed analytics call
		} finally {
			if (isPodcast) {
				navigate(to);
			} else {
				analytics.handleClickEvent(viewUrl);
				window.open(viewUrl, '_blank', 'noopener');
			}
		}
	}, [isPodcast, documentId, navigate, to, analytics, viewUrl]);

	const handleDownloadClick = useCallback(async () => {
		try {
			await documentService.trackDocumentDownload(documentId).fetch();
		} catch (error) {
			// fail silently because we dont need to report a failed analytics call
		} finally {
			analytics.handleClickEvent(downloadUrl);
			window.open(downloadUrl, '_blank', 'noopener');
		}
	}, [downloadUrl, documentId, analytics]);

	const handleShareButtonClick = () => {
		copyTextToClipboard(`${window.location.origin}${shareUrl}`, 'Link copied to clipboard.');
	};

	const handleBookmarkButtonClick = useCallback(
		async (active: boolean, message: string) => {
			setIsBookmarking(true);

			try {
				if (!account) {
					throw new Error('account is undefined.');
				}

				const { bookmark } = await accountService
					.updateAccountBookmarkContent(account.accountId, {
						bookmarkType,
						contentId: documentId,
						activeFlag: active,
					})
					.fetch();

				onChange?.(bookmark);

				showAlert({
					variant: 'success',
					children: <p className="mb-0 text-white">{message}</p>,
				});
			} catch (error) {
				handleError(error);
			} finally {
				setIsBookmarking(false);
			}
		},
		[account, bookmarkType, documentId, handleError, onChange, showAlert]
	);

	return (
		<div className="mb-16">
			<Row className="mb-5">
				<Col md={7}>
					<div className="mb-2 d-flex align-items-center justify-content-between">
						<h4 className="mb-0 d-flex align-items-center text-muted">{category}</h4>
						<div className="d-flex align-items-center text-primary">
							{viewUrl && (
								<Button variant="link" className="mr-1" onClick={handleViewClick}>
									<FileEarmarkText />
								</Button>
							)}
							{navigationConfig?.allowDownloads && canDownload && downloadUrl && (
								<Button className="mx-1" variant="link" onClick={handleDownloadClick}>
									<Download />
								</Button>
							)}
							<Button variant="link" className="mx-1" onClick={handleShareButtonClick}>
								<Copy />
							</Button>
							{isBookmarked ? (
								<LoadingButton
									isLoading={isBookmarking}
									variant="link"
									className="ml-1"
									onClick={() => {
										handleBookmarkButtonClick(false, 'Bookmark removed.');
									}}
									disabled={isBookmarking}
								>
									<BookmarkFill className="d-flex" />
								</LoadingButton>
							) : (
								<LoadingButton
									isLoading={isBookmarking}
									variant="link"
									className="ml-1"
									onClick={() => {
										handleBookmarkButtonClick(true, 'Bookmark saved.');
									}}
									disabled={isBookmarking}
								>
									<Bookmark className="d-flex" />
								</LoadingButton>
							)}
						</div>
					</div>
					<h3 className="mb-5">
						<Link to={to}>{title}</Link>
					</h3>
					<ResponsiveEllipsis text={description ?? ''} maxLine="4" component="p" />
				</Col>
				<Col md={5}>
					<div className={classes.imageOuter} style={{ backgroundImage: `url(${imageSrc})` }}></div>
				</Col>
			</Row>
			<Row>
				<Col>
					<div className="d-flex flex-wrap">
						<h4 className="mb-0 d-flex align-items-center text-muted">
							{date} {tags && tags.length > 0 && <span className="mx-1">&bull;</span>}
						</h4>
						{tags &&
							tags.map((tag, index) => (
								<span
									key={index}
									className={`small font-weight-semi-bold text-muted ${classes.tagItem}`}
								>
									{tag}
								</span>
							))}
					</div>
				</Col>
			</Row>
		</div>
	);
};
